import { type DiscoverChatbotProfileOverlayViewProps } from "@next-platform/client-common-core";
import {
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  DropDownMenu,
  DropDownMenuItem,
  IconVariants,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { Skeleton } from "@nextui-org/react";
import React, { useMemo } from "react";

import formatDiscoverChatbotCardCreatorHandle from "./formatDiscoverChatbotCardCreatorHandle";

// --- CLASS CONSTANTS
const CLASS_ROOT = "flex flex-row justify-between sm:w-[calc(100%-190px)] gap-1 w-full";
const CLASS_OVERLAY_TITLE_HEADING = "my-0 w-fit overflow-hidden text-ellipsis";
const CLASS_CREATOR_LINK_BUTTON = "cursor-pointer bg-transparent max-w-full";
const CLASS_CREATOR_HANDLE_TEXT = "text-blue-10 w-full overflow-hidden text-ellipsis";
const CLASS_HEADER_ACTIONS = "flex gap-2 items-start absolute right-2.5 top-2.5";
const CLASS_ICON_FAVORITE_FILLED = "text-red-9 fill-red-9";
const CLASS_ICON_FAVORITE_OUTLINED = "dark:text-white dark:fill-transparent text-black fill-transparent";
const CLASS_CLOSE_BUTTON = "desktop:hidden";
const CLASS_OVERLAY_TITLE_CONTAINER = "flex items-center gap-2";
const CLASS_RECOMMENDED_CONTAINER = "w-fit mb-1 flex justify-center items-center px-2 rounded-full bg-purple-9";
const CLASS_RECOMMENDED_TEXT = "text-white text-nowrap";
const CLASS_HANDLE_SKELETON = `mt-1 ${CLASS_CREATOR_HANDLE_TEXT} w-[40%] h-[1rem] rounded`;
const CLASS_OVERLAY_TITLE_WRAPPER = "w-full";

const CLASSNAMES_ICON_FAVORITE_FILLED = { icon: CLASS_ICON_FAVORITE_FILLED };
const CLASSNAMES_ICON_FAVORITE_OUTLINED = { icon: CLASS_ICON_FAVORITE_OUTLINED };

type DiscoverChatbotProfileOverlayHeaderProps = Pick<
  DiscoverChatbotProfileOverlayViewProps,
  | "labels"
  | "canFavorite"
  | "isFavorite"
  | "isFavoritePending"
  | "canShare"
  | "canBlockCreator"
  | "canReport"
  | "canLessLikeThis"
  | "onClose"
  | "onFavorite"
  | "onShare"
  | "onBlockCreator"
  | "onReport"
  | "onLessLikeThis"
  | "onCreatorPress"
  | "canOpenCreator"
> & {
  readonly name: string;
  readonly creatorUsername?: string;
  readonly testID?: string;
  readonly headingId?: string;
  readonly isRecomended?: boolean;
  readonly loading: boolean;
};

type MenuOptions = Pick<
  DiscoverChatbotProfileOverlayHeaderProps,
  "labels" | "canBlockCreator" | "canReport" | "canLessLikeThis" | "onBlockCreator" | "onBlockCreator" | "onReport" | "onLessLikeThis"
>;

type OverlayTitleProps = {
  readonly creatorName: string;
  readonly creatorHandle?: string;
  readonly canOpenCreator: boolean;
  readonly onCreatorPress: () => void;
  readonly testID: string;
  readonly isRecomended?: boolean;
  readonly recommendedLabel: string;
  readonly headingId?: string;
  readonly loading: boolean;
};

const DEFAULT_TEST_ID = "DiscoverChatbotProfileOverlayView";

// Share has its own header button, so the menu only lists the moderation actions.
const buildMenuItems = ({
  labels,
  canBlockCreator,
  canReport,
  canLessLikeThis,
  onBlockCreator,
  onReport,
  onLessLikeThis,
}: MenuOptions): DropDownMenuItem[] => {
  const menuItems: DropDownMenuItem[] = [];

  if (canBlockCreator) {
    menuItems.push({ label: labels.blockCreator, icon: IconVariants.USER_CANCEL, onPress: onBlockCreator });
  }

  if (canReport) {
    menuItems.push({ label: labels.report, icon: IconVariants.FLAG, onPress: onReport });
  }

  if (canLessLikeThis) {
    menuItems.push({ label: labels.lessLikeThis, icon: IconVariants.CIRCLE_MINUS, onPress: onLessLikeThis });
  }

  return menuItems;
};

const OverlayTitle = ({
  creatorName,
  creatorHandle,
  canOpenCreator,
  onCreatorPress,
  testID,
  headingId,
  isRecomended,
  recommendedLabel,
  loading,
}: OverlayTitleProps) => {
  let handleContent = null;

  if (loading) {
    handleContent = <Skeleton className={CLASS_HANDLE_SKELETON} data-testid={`${testID}-HandleSkeleton`} />;
  } else if (canOpenCreator && creatorHandle) {
    handleContent = (
      <button data-testid={`${testID}-CreatorLink`} className={CLASS_CREATOR_LINK_BUTTON} onClick={onCreatorPress}>
        <Text variant={TextVariants.LABEL_XSMALL_REGULAR} className={CLASS_CREATOR_HANDLE_TEXT}>
          {creatorHandle}
        </Text>
      </button>
    );
  }

  return (
    <>
      <div className={CLASS_OVERLAY_TITLE_CONTAINER}>
        <Text id={headingId} as="h3" variant={TextVariants.HEADING_4_BOLD} className={CLASS_OVERLAY_TITLE_HEADING}>
          {creatorName}
        </Text>
        {isRecomended ? (
          <div className={CLASS_RECOMMENDED_CONTAINER}>
            <Text as="span" variant={TextVariants.LABEL_XSMALL_BOLD} className={CLASS_RECOMMENDED_TEXT}>
              {recommendedLabel}
            </Text>
          </div>
        ) : null}
      </div>

      {handleContent}
    </>
  );
};

const DiscoverChatbotProfileOverlayHeader = ({
  labels,
  canFavorite,
  isFavorite,
  canBlockCreator,
  canReport,
  canLessLikeThis,
  isFavoritePending,
  onClose,
  onFavorite,
  onShare,
  onBlockCreator,
  onCreatorPress,
  onReport,
  onLessLikeThis,
  canOpenCreator,
  canShare,
  name,
  creatorUsername,
  headingId,
  isRecomended,
  loading,
  testID = DEFAULT_TEST_ID,
}: DiscoverChatbotProfileOverlayHeaderProps) => {
  const menuItems = useMemo(
    () => buildMenuItems({ labels, canBlockCreator, canReport, canLessLikeThis, onBlockCreator, onLessLikeThis, onReport }),
    [labels, canBlockCreator, canReport, canLessLikeThis, onBlockCreator, onLessLikeThis, onReport],
  );
  const creatorHandle = formatDiscoverChatbotCardCreatorHandle(creatorUsername);

  // Memoize classNames for the favorite button to avoid passing new objects to JSX attributes
  const favoriteButtonClassNames = isFavorite ? CLASSNAMES_ICON_FAVORITE_FILLED : CLASSNAMES_ICON_FAVORITE_OUTLINED;

  return (
    <div className={CLASS_ROOT}>
      <div className={CLASS_OVERLAY_TITLE_WRAPPER}>
        <OverlayTitle
          creatorName={name}
          canOpenCreator={canOpenCreator}
          onCreatorPress={onCreatorPress}
          testID={testID}
          headingId={headingId}
          creatorHandle={creatorHandle}
          isRecomended={isRecomended}
          recommendedLabel={labels.recommended}
          loading={loading}
        />
      </div>
      <div className={CLASS_HEADER_ACTIONS}>
        {canFavorite ? (
          <ButtonIcon
            onClick={onFavorite}
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.MEDIUM}
            icon={IconVariants.FAVORITE}
            disabled={isFavoritePending}
            classNames={favoriteButtonClassNames}
            aria-label={isFavorite ? labels.favorited : labels.favorite}
          />
        ) : null}
        {canShare ? (
          <ButtonIcon
            onClick={onShare}
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.MEDIUM}
            icon={IconVariants.SHARE}
            aria-label={labels.share}
          />
        ) : null}
        {menuItems.length > 0 ? (
          <DropDownMenu listItems={menuItems}>
            <ButtonIcon variant={ButtonVariant.SECONDARY} size={ButtonSize.MEDIUM} icon={IconVariants.THREE_DOTS} aria-label={labels.more} />
          </DropDownMenu>
        ) : null}
        <ButtonIcon
          onClick={onClose}
          variant={ButtonVariant.SECONDARY}
          size={ButtonSize.MEDIUM}
          icon={IconVariants.CHEVRON_DOWN}
          aria-label={labels.close}
          data-testid={`${testID}-CloseButton`}
          className={CLASS_CLOSE_BUTTON}
        />
      </div>
    </div>
  );
};

export default React.memo(DiscoverChatbotProfileOverlayHeader);
